<template>
  <div
    class="details-grid"
    :class="{
      'details-grid--left-sidebar': leftSidebar,
      'details-grid--loading-sidebar': loadingSidebar,
      'details-grid--direction-column': isDirectionColumn ?? isMobile,
    }"
  >
    <div
      class="details-grid__content"
      :class="{
        'details-grid__content--wide': wide,
      }"
    >
      <slot />
    </div>
    <div
      v-if="!hideSidebar"
      ref="sidebar"
      class="details-grid__sidebar"
      :class="{
        [`details-grid__sidebar--${sidebarSize}`]: sidebarSize,
        'details-grid__sidebar--no-padding': withoutPadding,
        'details-grid__sidebar--wide': wide,
      }"
    >
      <slot name="sidebar" />
    </div>
  </div>
</template>

<script lang="ts">
import { ref, defineComponent, onMounted, onBeforeUnmount } from '@/composition/vue/compositionApi'
import { useBreakpoints } from '@/composition/breakpoints'

export default defineComponent({
  props: {
    leftSidebar: {
      type: Boolean,
    },
    withoutPadding: {
      type: Boolean,
    },
    loadingSidebar: {
      type: Boolean,
    },
    hideSidebar: {
      type: Boolean,
      default: false,
    },
    wide: {
      type: Boolean,
    },
    isDirectionColumn: {
      type: Boolean,
      default: undefined,
    },
  },
  setup() {
    const { isMobile } = useBreakpoints()

    const sidebarSize = ref<'md' | 'sm' | ''>('')
    const sidebar = ref()
    let resizeObserver: ResizeObserver

    const updateSidebarSize = () => {
      const width = sidebar.value.offsetWidth

      if (width < 390 && width >= 320) {
        sidebarSize.value = 'md'
      } else if (width < 320) {
        sidebarSize.value = 'sm'
      } else {
        sidebarSize.value = ''
      }
    }

    onMounted(() => {
      if (!sidebar.value) return
      resizeObserver = new ResizeObserver(() => {
        updateSidebarSize()
      })

      resizeObserver.observe(sidebar.value)
    })

    onBeforeUnmount(() => {
      resizeObserver?.disconnect()
    })

    return {
      sidebar,
      sidebarSize,
      isMobile,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

$sidebarMaxWidth: 408px;
$sidebarMinWidth: 240px;
$contentMinWidth: 445px;
$sidebarWideMaxWidth: 500px;
$gap: 20px;
$extraOffsetForFocus: 8px;

$contentMaxWidth: $pageBlocksWidth - $sidebarMaxWidth - $gap + $extraOffsetForFocus;

.details-grid {
  display: flex;
  align-items: flex-start;
  gap: $gap;

  &__content {
    flex-shrink: 1;
    width: calc(100% + $extraOffsetForFocus);
    min-width: $contentMinWidth;
    max-width: $contentMaxWidth;
    margin: -4px;
    padding: 4px;

    &--wide {
      flex-shrink: 6;
      max-width: unset;
    }
  }

  &--left-sidebar {
    .details-grid__content {
      order: 1;
    }
  }

  &__sidebar {
    width: 100%;
    flex-shrink: 10;
    max-width: $sidebarMaxWidth;
    min-width: $sidebarMinWidth;
    background: $white;
    padding: 32px;
    border: 1px solid $input;
    border-radius: $border-radius-md;

    &--wide {
      max-width: $sidebarWideMaxWidth;
      min-width: 440px;

      @include media-xl-max {
        max-width: 440px;
        min-width: 370px;
      }

      @include media-lg-max {
        max-width: 370px;
        min-width: 326px;
      }

      @include media-md-max {
        max-width: 326px;
        min-width: 280px;
      }
    }

    &--md,
    &--sm {
      padding: 32px 24px;
    }

    &--sm {
      &:deep(.activity-tile__title) {
        font-size: $font-size-root;
      }
    }

    &--no-padding {
      padding: 0;
    }
  }

  &--loading-sidebar {
    .details-grid__sidebar {
      background: linear-gradient(180deg, $white 50%, rgba(255, 255, 255, 0) 100%);
      border: none;
      align-self: stretch;
    }
  }

  &--direction-column {
    flex-direction: column;
    gap: 16px;
    .details-grid {
      &__content {
        order: 1;
        min-width: unset;
        max-width: unset;
      }

      &__sidebar {
        max-width: unset;
        padding: 20px 16px;

        &--no-padding {
          padding: 0;
        }
      }
    }
  }
}
</style>
